import { defineStore } from 'pinia';
import { getSupportedFiats, onrampOrder, getOnrampOrders } from '@/api/recharge';
import { flattenSupportedFiats, normalizeSupportedFiatRows } from '@/domain/deposit/fiat-options';
import {
  buildCashFiatPerUsdByCurrency,
  cashFiatUsesFxRate,
  clampFiatAmountToPayMin,
  convertCashAmountByPseudoRate,
  findUsdFiatRow,
  mergeCashFiatRates,
  resolveDefaultFiatRow,
  resolveInitialCashAmountUsdBase,
  resolveSwitchCreditUsd,
} from '@/utils/deposit-fiat-amount';
import { ensureOpenErUsdRates, peekOpenErUsdRates } from '@/utils/open-er-fx';
import { resolveCountryCodeFromCurrentIp, peekCachedCurrentIpCountryCode } from '@/utils/ip-country-lookup';
import {
  persistCheckoutModePreference,
  readPreferredCheckoutMode,
} from '@/utils/deposit-onramp-checkout';
import { resolveOnrampReturnUrl } from '@/utils/deposit-onramp-return';
import {
  ONRAMP_ORDER_POLL_MAX_MS,
  ONRAMP_ORDER_POLL_MS,
  isOnrampReturnTerminalPhase,
  lookupOnrampOrderById,
  normalizeOnrampOrderStatus,
  resolveOnrampReturnPhase,
} from '@/utils/onramp-order-status';
import { isRequestTimeoutLike, withRetry } from '@/utils/request-retry';
import { useTopPayDepositStore } from '@/stores/toppayDeposit';

/** 银行卡充值最低金额（U / 与法币输入同单位） */
export const CASH_DEPOSIT_MIN_U = 5;
/** 银行卡充值默认金额（USD 等值，其它法币按通道汇率换算） */
export const CASH_DEPOSIT_DEFAULT_USD = 20;

function readCashFiatPerUsdByCurrency() {
  try {
    return mergeCashFiatRates(
      buildCashFiatPerUsdByCurrency(useTopPayDepositStore().regions),
      peekOpenErUsdRates()
    );
  } catch {
    return mergeCashFiatRates({ USD: 1 }, peekOpenErUsdRates());
  }
}

let supportedFiatsLoadPromise = null;
let onrampReturnPollTimer = null;
let onrampReturnPollInflight = false;
let onrampReturnPollStartedAt = 0;
let onrampReturnPollGeneration = 0;

async function resolveFiatCountryCode(presetCountryCode, { forceRefresh = false } = {}) {
  if (presetCountryCode !== undefined) {
    return presetCountryCode ?? null;
  }
  return resolveCountryCodeFromCurrentIp({ forceRefresh }).catch(() => null);
}

/** 列表已在时用缓存国家码立刻选中法币，不把现金页先画成 USD */
export function prepareCashFiatFromCache(store) {
  if (!store?.supportedFiatRows?.length) return;
  const cached = peekCachedCurrentIpCountryCode();
  if (!cached) return;
  const prevKey = store.selectedFiatKey;
  store.applyFiatGeoDefault(cached);
  if (!String(store.amount ?? '').trim() || store.selectedFiatKey !== prevKey) {
    store.applyCashAmountDefault();
  }
}

/** 打开弹窗 / 进现金页前：尽量先选好法币 */
export async function ensureCashFiatReady(store, { forceRefreshIp = false } = {}) {
  if (!store) return;
  const fxPromise = ensureOpenErUsdRates().catch(() => ({}));
  if (!forceRefreshIp) {
    prepareCashFiatFromCache(store);
    if (store.supportedRowsRaw.length && store.selectedFiatKey) {
      await fxPromise;
      return;
    }
  }

  if (store.supportedRowsRaw.length) {
    const countryCode = await resolveCountryCodeFromCurrentIp({
      forceRefresh: forceRefreshIp,
    }).catch(() => null);
    await fxPromise;
    const prevKey = store.selectedFiatKey;
    store.applyFiatGeoDefault(countryCode);
    if (!String(store.amount ?? '').trim() || store.selectedFiatKey !== prevKey) {
      store.applyCashAmountDefault();
    }
    return;
  }

  await Promise.all([
    fxPromise,
    store.loadSupportedFiats({
      applyGeoDefault: true,
      applyDefaultAmount: true,
      isCashAmountStep: true,
      forceRefreshIp,
    }),
  ]);
}

export const useFiatDepositStore = defineStore('fiatDeposit', {
  state: () => ({
    supportedRowsRaw: [],
    listLoading: false,
    listFetchError: '',
    selectedFiatKey: '',
    amount: '',
    /** 切币锚点：有汇率的币从金额反推，Alchemy 无汇率币沿用此值 */
    cashCreditUsd: 0,
    orderCreating: false,
    orderError: '',
    checkoutUrl: '',
    orderId: '',
    returnedAt: null,
    /** @type {'pending'|'pay_success'|'finished'|'failed'|'neutral'|''} */
    returnPhase: '',
    returnStatus: '',
    returnFailMessage: '',
    returnFiatAmount: null,
    returnFiat: '',
    returnCrypto: '',
    /** @type {'iframe' | 'popup'} */
    checkoutMode: 'iframe',
    popupBlocked: false,
  }),

  getters: {
    supportedFiatRows(state) {
      return normalizeSupportedFiatRows(state.supportedRowsRaw);
    },

    activeSelectedFiat(state) {
      const rows = normalizeSupportedFiatRows(state.supportedRowsRaw);
      return rows.find((item) => item.key === state.selectedFiatKey) ?? null;
    },

    activeCashCurrency(state) {
      const rows = normalizeSupportedFiatRows(state.supportedRowsRaw);
      const active = rows.find((item) => item.key === state.selectedFiatKey);
      return active?.currency || 'USD';
    },
    activeCashPayWays(state) {
      const rows = normalizeSupportedFiatRows(state.supportedRowsRaw);
      const active = rows.find((item) => item.key === state.selectedFiatKey);
      return active?.payWays || [];
    },

    cashAmountNum(state) {
      const n = parseFloat(state.amount);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },

    cashDepositMin(state) {
      const rows = normalizeSupportedFiatRows(state.supportedRowsRaw);
      const active = rows.find((item) => item.key === state.selectedFiatKey);
      const apiMin = Number(active?.payMin);
      const safeApiMin = Number.isFinite(apiMin) && apiMin > 0 ? apiMin : 0;
      return Math.max(CASH_DEPOSIT_MIN_U, safeApiMin);
    },
  },

  actions: {
    clearListFetchError() {
      this.listFetchError = '';
    },

    clearOrderError() {
      this.orderError = '';
    },

    setAmount(value) {
      this.amount = String(value ?? '');
      const fiat = this.activeSelectedFiat;
      const rates = readCashFiatPerUsdByCurrency();
      if (!fiat || !cashFiatUsesFxRate(fiat.currency, rates)) return;
      const usd = resolveSwitchCreditUsd(this.cashAmountNum, fiat, {
        usMinFallback: CASH_DEPOSIT_MIN_U,
        fiatPerUsdByCurrency: rates,
      });
      if (usd > 0) this.cashCreditUsd = usd;
    },

    /**
     * 失焦等：输入低于最小金额时抬到 min。
     * @param {number | null | undefined} [payMinOverride] 支付方式 payMin，缺省用 cashDepositMin
     */
    clampAmountToMin(payMinOverride) {
      const override = Number(payMinOverride);
      const min = Number.isFinite(override) && override > 0 ? override : this.cashDepositMin;
      const next = clampFiatAmountToPayMin(this.amount, min, this.activeCashCurrency);
      if (next !== this.amount) this.amount = next;
    },

    applyFiatGeoDefault(countryCode) {
      const rows = this.supportedFiatRows;
      if (!rows.length) return;
      const pick = resolveDefaultFiatRow(rows, countryCode);
      if (!pick) return;
      if (process.env.NODE_ENV === 'development') {
        console.debug('[fiat-geo]', {
          countryCode: countryCode || null,
          currency: pick.currency,
          country: pick.country,
          key: pick.key,
        });
      }
      this.selectedFiatKey = pick.key;
    },

    applyCashAmountDefault() {
      const fiat = this.activeSelectedFiat;
      if (!fiat) {
        this.amount = String(CASH_DEPOSIT_DEFAULT_USD);
        this.cashCreditUsd = CASH_DEPOSIT_DEFAULT_USD;
        return;
      }
      this.amount = resolveInitialCashAmountUsdBase(fiat, {
        defaultUsd: CASH_DEPOSIT_DEFAULT_USD,
        usMinFallback: CASH_DEPOSIT_MIN_U,
        usFiatRow: findUsdFiatRow(this.supportedFiatRows),
        fiatPerUsdByCurrency: readCashFiatPerUsdByCurrency(),
      });
      this.cashCreditUsd = CASH_DEPOSIT_DEFAULT_USD;
    },

    /** 法币金额步骤一次性 bootstrap：拉列表 + geo 默认法币 + 默认金额 */
    async bootstrapAmountStep() {
      await ensureOpenErUsdRates().catch(() => ({}));
      if (this.supportedRowsRaw.length) {
        const countryCode = await resolveFiatCountryCode(undefined, { forceRefresh: true });
        this.applyFiatGeoDefault(countryCode);
        this.applyCashAmountDefault();
        return;
      }
      return this.loadSupportedFiats({
        applyDefaultAmount: true,
        applyGeoDefault: true,
        isCashAmountStep: true,
        forceRefreshIp: true,
      });
    },

    /** 登录 / 刷新后预拉法币列表（含出口 IP 默认法币），金额在首次进入金额步或 prefetch 时补齐 */
    async prefetchForSession() {
      await this.loadSupportedFiats({
        applyGeoDefault: true,
        applyDefaultAmount: true,
        isCashAmountStep: true,
      });
    },

    /**
     * @param {{
     *   applyDefaultAmount?: boolean,
     *   applyGeoDefault?: boolean,
     *   isCashAmountStep?: boolean,
     *   countryCode?: string | null,
     *   forceRefreshIp?: boolean,
     * }} [options]
     */
    async loadSupportedFiats(options = {}) {
      const applyDefaultAmount = options.applyDefaultAmount === true;
      const applyGeoDefault = options.applyGeoDefault === true;
      const isCashAmountStep = options.isCashAmountStep === true;
      const presetCountryCode = options.countryCode;
      const forceRefreshIp = options.forceRefreshIp === true;

      if (supportedFiatsLoadPromise) {
        await Promise.all([
          supportedFiatsLoadPromise,
          ensureOpenErUsdRates().catch(() => ({})),
        ]);
        if (applyGeoDefault) {
          const countryCode = await resolveFiatCountryCode(presetCountryCode, {
            forceRefresh: forceRefreshIp,
          });
          this.applyFiatGeoDefault(countryCode);
        }
        if (applyDefaultAmount && isCashAmountStep) {
          this.applyCashAmountDefault();
        }
        return;
      }

      this.listLoading = true;
      this.listFetchError = '';

      supportedFiatsLoadPromise = (async () => {
        try {
          const countryPromise = applyGeoDefault
            ? resolveFiatCountryCode(presetCountryCode, { forceRefresh: forceRefreshIp })
            : Promise.resolve(null);
          const [res, countryCode] = await Promise.all([
            withRetry(() => getSupportedFiats(), { retries: 2, delayMs: 800 }),
            countryPromise,
            ensureOpenErUsdRates().catch(() => ({})),
          ]);
          const rows = flattenSupportedFiats(res);
          this.supportedRowsRaw = rows;

          if (rows.length && applyGeoDefault) {
            this.applyFiatGeoDefault(countryCode);
          }

          if (applyDefaultAmount && isCashAmountStep) {
            this.applyCashAmountDefault();
          }
        } catch (err) {
          this.supportedRowsRaw = [];
          // 超时不写 listFetchError，避免落地页弹出 axios 原文
          if (isRequestTimeoutLike(err)) {
            console.warn('[fiat-deposit] supported fiats timeout after retry', err);
            this.listFetchError = '';
          } else {
            this.listFetchError = err?.message || 'Failed to load supported fiats';
          }
        } finally {
          this.listLoading = false;
          supportedFiatsLoadPromise = null;
        }
      })();

      await supportedFiatsLoadPromise;
    },

    /**
     * @param {string} key
     * @param {{ prevAmount?: number }} [options]
     */
    selectFiat(key, options = {}) {
      const prevFiat = this.activeSelectedFiat;
      const prevAmount =
        Number(options.prevAmount) > 0 ? Number(options.prevAmount) : this.cashAmountNum;

      this.selectedFiatKey = String(key ?? '').trim();
      const fiat = this.activeSelectedFiat;
      if (!fiat) return;

      if (prevFiat && prevFiat.key !== fiat.key && prevAmount > 0) {
        const rates = readCashFiatPerUsdByCurrency();
        const opts = {
          usMinFallback: CASH_DEPOSIT_MIN_U,
          fiatPerUsdByCurrency: rates,
          knownCreditUsd: this.cashCreditUsd,
        };
        const usd = resolveSwitchCreditUsd(prevAmount, prevFiat, opts);
        this.amount = convertCashAmountByPseudoRate(prevAmount, prevFiat, fiat, opts);
        if (usd > 0) this.cashCreditUsd = usd;
        return;
      }
      this.applyCashAmountDefault();
    },

    /**
     * 创建法币 onramp 订单。成功返回 checkoutUrl；失败写 orderError 并返回 null。
     * @param {{ redirectUrl?: string }} [options]
     * @returns {Promise<string | null>}
     */
    async createOnrampOrder(options = {}) {
      if (this.orderCreating) return null;

      const fiat = this.activeSelectedFiat;
      if (!fiat) {
        this.orderError = 'Please select a fiat currency';
        return null;
      }

      if (this.cashAmountNum < this.cashDepositMin) {
        return null;
      }

      this.orderCreating = true;
      this.orderError = '';
      this.checkoutUrl = '';
      this.orderId = '';

      try {
        const redirectUrl =
          String(options.redirectUrl ?? '').trim() ||
          resolveOnrampReturnUrl() ||
          (typeof window !== 'undefined' ? window.location.href : '');
        const walletAddress = String(
          options.walletAddress ?? options.userAddress ?? options.address ?? ''
        ).trim();
        const payload = {
          fiat: fiat.currency || 'USD',
          fiatAmount: this.cashAmountNum,
          language: 'en-US',
          payWayCode: options.payWayCode || '10001',
          redirectUrl,
          email: options.email,
        };
        if (walletAddress) {
          payload.walletAddress = walletAddress;
          payload.userAddress = walletAddress;
          payload.depositAddress = walletAddress;
          payload.address = walletAddress;
        }
        const res = await onrampOrder(payload);
        const checkoutUrl = String(res?.checkoutUrl || '').trim();
        if (!checkoutUrl) {
          this.orderError = 'Failed to get checkout url';
          return null;
        }
        this.checkoutUrl = checkoutUrl;
        this.orderId = String(res?.merchantOrderNo ?? res?.orderId ?? res?.id ?? '').trim();
        return checkoutUrl;
      } catch (err) {
        this.orderError = err?.message || 'Failed to create onramp order';
        return null;
      } finally {
        this.orderCreating = false;
      }
    },

    clearReturnStatus() {
      this.stopReturnStatusPoll();
      this.returnPhase = '';
      this.returnStatus = '';
      this.returnFailMessage = '';
      this.returnFiatAmount = null;
      this.returnFiat = '';
      this.returnCrypto = '';
    },

    applyReturnOrder(order, { timedOut = false } = {}) {
      const status = normalizeOnrampOrderStatus(order?.status);
      this.returnStatus = status;
      this.returnFailMessage = String(order?.failMessage ?? '').trim();
      const amount = Number(order?.fiatAmount);
      this.returnFiatAmount = Number.isFinite(amount) && amount > 0 ? amount : null;
      this.returnFiat = String(order?.fiat ?? '').trim();
      this.returnCrypto = String(order?.crypto ?? '').trim();
      this.returnPhase = resolveOnrampReturnPhase(status, { timedOut });
    },

    stopReturnStatusPoll() {
      onrampReturnPollGeneration += 1;
      if (onrampReturnPollTimer != null) {
        clearInterval(onrampReturnPollTimer);
        onrampReturnPollTimer = null;
      }
      onrampReturnPollInflight = false;
      onrampReturnPollStartedAt = 0;
    },

    async tickReturnStatusPoll() {
      if (onrampReturnPollInflight) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (!this.returnedAt) {
        this.stopReturnStatusPoll();
        return;
      }

      const elapsed = Date.now() - onrampReturnPollStartedAt;
      const timedOut = elapsed >= ONRAMP_ORDER_POLL_MAX_MS;

      const generation = onrampReturnPollGeneration;
      const authGeneration = getAuthGeneration();
      onrampReturnPollInflight = true;
      try {
        const { order, stale } = await lookupOnrampOrderById(
          this.orderId,
          (params) => getOnrampOrders(params),
          {
            isCurrent: () =>
              generation === onrampReturnPollGeneration && authGeneration === getAuthGeneration(),
          }
        );
        if (
          stale ||
          generation !== onrampReturnPollGeneration ||
          authGeneration !== getAuthGeneration()
        ) {
          return;
        }

        if (order) {
          this.applyReturnOrder(order, { timedOut: false });
          if (isOnrampReturnTerminalPhase(this.returnPhase)) {
            this.stopReturnStatusPoll();
          }
          return;
        }

        if (timedOut) {
          this.returnPhase = 'neutral';
          this.stopReturnStatusPoll();
        } else if (!this.returnPhase) {
          this.returnPhase = 'pending';
        }
      } catch (err) {
        console.warn('[fiat-deposit] onramp order poll failed', err);
        if (timedOut) {
          this.returnPhase = 'neutral';
          this.stopReturnStatusPoll();
        }
      } finally {
        if (generation === onrampReturnPollGeneration) {
          onrampReturnPollInflight = false;
        }
      }
    },

    startReturnStatusPoll() {
      this.stopReturnStatusPoll();
      onrampReturnPollGeneration += 1;
      this.returnPhase = 'pending';
      this.returnStatus = '';
      this.returnFailMessage = '';
      onrampReturnPollStartedAt = Date.now();
      onrampReturnPollTimer = setInterval(() => {
        void this.tickReturnStatusPoll();
      }, ONRAMP_ORDER_POLL_MS);
      void this.tickReturnStatusPoll();
    },

    markOnrampReturned() {
      this.returnedAt = Date.now();
      this.startReturnStatusPoll();
    },

    initCheckoutMode() {
      this.checkoutMode = readPreferredCheckoutMode();
      this.popupBlocked = false;
    },

    /** @param {'iframe' | 'popup'} mode */
    setCheckoutMode(mode, options = {}) {
      const next = mode === 'popup' ? 'popup' : 'iframe';
      this.checkoutMode = next;
      if (next === 'iframe') {
        this.popupBlocked = false;
      }
      if (options.persist === false) return;
      if (next === 'popup') {
        persistCheckoutModePreference('popup');
        return;
      }
      // iframe：清掉历史 popup 偏好，避免下次误进弹窗模式
      persistCheckoutModePreference('iframe');
    },

    markPopupBlocked(blocked = true) {
      this.popupBlocked = Boolean(blocked);
    },

    reset() {
      this.stopReturnStatusPoll();
      this.supportedRowsRaw = [];
      this.listLoading = false;
      this.listFetchError = '';
      this.selectedFiatKey = '';
      this.amount = '';
      this.orderCreating = false;
      this.orderError = '';
      this.checkoutUrl = '';
      this.orderId = '';
      this.returnedAt = null;
      this.returnPhase = '';
      this.returnStatus = '';
      this.returnFailMessage = '';
      this.returnFiatAmount = null;
      this.returnFiat = '';
      this.returnCrypto = '';
      this.checkoutMode = 'iframe';
      this.popupBlocked = false;
    },
  },
});
